import React, { useEffect, useState } from 'react';
import { Marker, Circle, Tooltip } from 'react-leaflet';
import L from 'leaflet';

// Create custom user location icon
const createUserLocationIcon = (accuracy = null) => {
  return L.divIcon({
    className: 'user-location-marker',
    html: `
      <div class="user-location-container">
        <div class="user-location-dot">
          <div class="user-location-pulse"></div>
        </div>
        ${accuracy && accuracy < 100 ? 
          `<div class="accuracy-indicator">${Math.round(accuracy)}m</div>` : 
          ''
        }
      </div>
    `,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
};

function UserLocationMarker({ 
  position, 
  accuracy = null, 
  showAccuracyCircle = true,
  autoCenter = false,
  map = null 
}) {
  const [userIcon, setUserIcon] = useState(null);

  // Create CSS styles for the custom marker
  useEffect(() => {
    // Check if styles are already added
    if (document.getElementById('user-location-styles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'user-location-styles';
    style.textContent = `
      .user-location-container {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
      }
      
      .user-location-dot {
        width: 16px;
        height: 16px;
        background: #2563eb;
        border: 3px solid #ffffff;
        border-radius: 50%;
        position: absolute;
        top: 0;
        left: 0;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
        z-index: 1000;
      }
      
      .user-location-pulse {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 24px;
        height: 24px;
        margin-top: -12px;
        margin-left: -12px;
        border: 2px solid #2563eb;
        border-radius: 50%;
        animation: user-location-pulse 2s infinite ease-out;
        opacity: 0;
      }
      
      @keyframes user-location-pulse {
        0% {
          opacity: 1;
          transform: scale(0.5);
        }
        50% {
          opacity: 0.6;
          transform: scale(1.2);
        }
        100% {
          opacity: 0;
          transform: scale(2);
        }
      }
      
      .accuracy-indicator {
        position: absolute;
        top: 24px;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(37, 99, 235, 0.9);
        color: white;
        padding: 2px 6px;
        border-radius: 10px;
        font-size: 10px;
        white-space: nowrap;
        pointer-events: none;
        z-index: 1001;
      }
      
      .leaflet-div-icon.user-location-marker {
        background: transparent;
        border: none;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const existingStyle = document.getElementById('user-location-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  // Update icon when accuracy changes
  useEffect(() => {
    setUserIcon(createUserLocationIcon(accuracy));
  }, [accuracy]);

  // Auto-center map on user location (optional)
  useEffect(() => {
    if (autoCenter && map && position) {
      map.setView(position, 14, { animate: true });
    }
  }, [autoCenter, map, position]);

  if (!position || !userIcon) {
    return null;
  }

  return (
    <>
      {/* User location marker */}
      <Marker
        position={position}
        icon={userIcon}
        zIndexOffset={1000} // Ensure it's above other markers
      >
        <Tooltip permanent={false} direction="top" offset={[0, -10]}>
          <div className="text-center">
            <div className="font-semibold text-blue-700">📍 Votre position</div>
            {accuracy && accuracy < 100 && (
              <div className="text-xs text-gray-600">
                Précision: ±{Math.round(accuracy)}m
              </div>
            )}
          </div>
        </Tooltip>
      </Marker>

      {/* Accuracy circle */}
      {showAccuracyCircle && accuracy && accuracy > 10 && (
        <Circle
          center={position}
          radius={accuracy}
          fillColor="#2563eb"
          fillOpacity={0.1}
          color="#2563eb"
          opacity={0.3}
          weight={1}
        />
      )}
    </>
  );
}

export default UserLocationMarker;